import React, { useState, useContext } from 'react'
import { X, PencilLine, Trash2 } from 'lucide-react'
import { toast } from 'react-toastify';
import { CantiqueContextAPI } from '../Context/CantiqueContext'
import * as Api from '../Services/Api'

const AdminAddLanguage = ({ isOpen, onClose }) => {
  // we no longer maintain a separate "languages" state here; the
  // context already keeps the authoritative list. this ensures every
  // consumer stays in sync without manual copying.
  const { allCantiques, refreshLanguages, availableLanguages } = useContext(CantiqueContextAPI);
  const [newLanguage, setNewLanguage] = useState('');
  const [editingName, setEditingName] = useState('');
  const [editingId, setEditingId] = useState(null);

  // derive languages with counts each render using the objects from
  // context (which include the id). this gives us everything needed for
  // editing/deleting and for display.
  const languages = availableLanguages.map(l => ({
    id: l.id,
    name: l.name,
    count: allCantiques.filter(c => c.langue === l.name).length
  }));

  const handleAddLanguage = async () => {
    if (newLanguage.trim() === '') return

    if (!languages.find(l => l.name.toLowerCase() === newLanguage.trim().toLowerCase())) {
      try {
        await Api.createLangue({ name: newLanguage.trim() });
        setNewLanguage('');
        // fetching fresh list will trigger a re-render everywhere
        refreshLanguages();
        toast.success("Langue bien créée");
      } catch (err) {
        console.error('add language', err);
        toast.error(`Erreur lors de la création de la langue : ${err.response?.data?.message || err.message}`);
      }
    }
  }

  const handleEditLanguage = (id) => {
    const lang = languages.find(l => l.id === id)
    if (lang) {
      setEditingId(id)
      setEditingName(lang.name)
    }
  }

  const handleSaveEdit = async () => {
    if (editingName.trim() === '') return
    
    if (!languages.find(l => l.id !== editingId && l.name.toLowerCase() === editingName.trim().toLowerCase())) {
      try {
        await Api.updateLangue(editingId, { name: editingName.trim() });
        setEditingId(null);
        setEditingName('');
        refreshLanguages();
        toast.success("Langue modifiée avec succès");
      } catch (err) {
        console.error('update language', err);
        toast.error(`Erreur lors de la modification de la langue : ${err.response?.data?.message || err.message}`);
      }
    }
  }

  const handleCancelEdit = () => {
    setEditingId(null)
    setEditingName('')
  }

  const handleDeleteLanguage = async (id) => {
    try {
      await Api.deleteLangue(id);
      refreshLanguages();
      toast.success("Langue supprimée avec succès");
    } catch (err) {
      console.error('delete language', err);
      toast.error(`Erreur lors de la suppression de la langue : ${err.response?.data?.message || err.message}`);
    }
  }

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose()
    }
  }

  if (!isOpen) return null

  return (
    <>
      <div className="addLanguageModal" onClick={handleBackdropClick}>
        <div className="addLanguageModal__container">
          <button className="addLanguageModal__close" onClick={onClose}>
            <X size={24} />
          </button>

          <h2 className="addLanguageModal__title">Gestion des langues</h2>
          <div className="addLanguageModal__subtitle">
            Ajouter, modifier ou supprimer les langues des cantiques
          </div>

          {/* Ajouter une nouvelle langue */}
          <div className="addLanguageModal__form">
            <h3 className="addLanguageModal__form__title">Ajouter une nouvelle langue</h3>
            <div className="addLanguageModal__form__group">
              <input
                type="text"
                className="addLanguageModal__form__input"
                placeholder="Nom de la langue (ex: Français, Lingala, Swahili)"
                value={newLanguage}
                onChange={(e) => setNewLanguage(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleAddLanguage()}
              />
              <button
                className="addLanguageModal__form__btn"
                onClick={handleAddLanguage}
              >
                Ajouter
              </button>
            </div>
          </div>

          {/* Liste des langues */}
          <div className="addLanguageModal__list">
            <h3 className="addLanguageModal__list__title">
              Langues enregistrées ({languages.length})
            </h3>

            {languages.length === 0 ? (
              <div className="addLanguageModal__list__empty">
                Aucune langue enregistrée
              </div>
            ) : (
              <div className="addLanguageModal__list__items">
                {languages.map((lang) => (
                  <div key={lang.id} className="addLanguageModal__lang__item">
                    <div className="addLanguageModal__lang__item__info">
                      <div className="addLanguageModal__lang__item__name">
                        {lang.name}
                      </div>
                      <div className="addLanguageModal__lang__item__count">
                        {lang.count} cantique{lang.count !== 1 ? 's' : ''}
                      </div>
                    </div>
                    <div className="addLanguageModal__lang__item__actions">
                      <button
                        className="addLanguageModal__lang__item__btn addLanguageModal__lang__item__btn--edit"
                        onClick={() => handleEditLanguage(lang.id)}
                      >
                        <PencilLine size={14} />
                      </button>
                      <button
                        className="addLanguageModal__lang__item__btn addLanguageModal__lang__item__btn--delete"
                        onClick={() => handleDeleteLanguage(lang.id)}
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Modal de modification */}
      {editingId !== null && (
        <div className="addLanguageModal__edit__modal" onClick={(e) => {
          if (e.target === e.currentTarget) handleCancelEdit()
        }}>
          <div className="addLanguageModal__edit__container">
            <button className="addLanguageModal__edit__close" onClick={handleCancelEdit}>
              <X size={24} />
            </button>

            <h3 className="addLanguageModal__edit__title">Modifier la langue</h3>
            <div className="addLanguageModal__edit__subtitle">
              Modifiez le nom de la langue
            </div>

            <div className="addLanguageModal__edit__group">
              <label htmlFor="lang-name">Nom de la langue</label>
              <input
                id="lang-name"
                type="text"
                value={editingName}
                onChange={(e) => setEditingName(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSaveEdit()}
                autoFocus
              />
            </div>

            <div className="addLanguageModal__edit__actions">
              <button onClick={handleSaveEdit}>Enregistrer</button>
              <button onClick={handleCancelEdit}>Annuler</button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export default AdminAddLanguage